import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import { useEffect, useRef, useState } from "react";
import { Alert, FormControl, MenuItem, Select, Switch } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { PayoutAPI } from "../../api/PayoutAPI";
import PayoutDashboard from "../../pages/payout-management/PayoutDashboard";
import { stepperAction } from "../../redux/reducer/stepperReducer";
import { PaymentAPI } from "../../api/PaymentAPI";
import Loader from "../../component/Loader";
import { AccountSetupAPI } from "../../api/AccountSetupAPI";
import { isValidSetupData, vendorStepPayload } from "../../util/accountSetup";
import { CUSTOMER_PAY_FEE_COPY, FEE_FALLBACK, isFreePlanSetup, platformFeeLabel, vendorGuideCopy } from "../../util/accountSetupCopy";
import { VendorSetupGuide } from "../account-setup/SetupGuide";
import { PlaftFormAPI } from "../../api/PlatformAPI";
import { PrivilegeAPI } from "../../api/PrivilegesAPI";
import { Privileges } from "../../util/PrivilegeConstants";
import CustomSwitch from '../CustomComponents/CustomSwitch/CustomSwitch.jsx'
import { rootAction } from "../../redux/reducer/rootReducer";
import { rateLimitFailureMessage } from "../auth/emailVerification";

const appearance = {
    variables: {
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        colorPrimary: "#8fbd04",
        buttonPrimaryColorText: "#000",
    },
};
const PayoutForm = ({ setIsUpdated, applySetupData }) => {
    const { configId, stepperDetails } = useSelector((state) => state.stepper);
    const [isUserValid, setIsUserValid] = useState(false);
    const [stripeConnectInstance, setStripeConnectInstance] = useState(() => { });
    const [paymentMethod, setpaymentMethod] = useState('Stripe');
    const [toggle, setToggle] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [privileges, setPrivileges] = useState({});
    const [isCustomerPayFee, setIsCustomerPayFee] = useState(false)
    const [feeLabels, setFeeLabels] = useState({
        free: FEE_FALLBACK.FREE,
        paid: FEE_FALLBACK.PAID,
    })
    const [requiredDocuments, setRequiredDocuments] = useState({
        dob: false,
        ssn: false,
        govtId: false,
        companyTaxId: false
    })
    const dispatch = useDispatch();
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const payoutSetupBlockedRef = useRef(false);

    const fetchClientSecret = async (userPayload) => {
        if (payoutSetupBlockedRef.current) {
            return null;
        }
        try {
            const { Data: clientSecret } = await PayoutAPI.postAdminPayoutSetup(userPayload)
            return clientSecret
        } catch (error) {
            const limited = rateLimitFailureMessage(error);
            if (limited) {
                payoutSetupBlockedRef.current = true;
                dispatch(rootAction.setWarningMessage({
                    id: Date.now(),
                    type: "error",
                    content: limited,
                }));
            }
            return null;
        }
    }

    const updateStepper = async () => {
        let response = await AccountSetupAPI.postStepper(vendorStepPayload(selectedClub.clubId));

        if (isValidSetupData(response?.Data) && typeof applySetupData === 'function') {
            applySetupData(response.Data);
            return;
        }
        if (response?.Status == 200) {
            if (typeof setIsUpdated === 'function') {
                setIsUpdated(true);
            }
        }

    }


    const isUserAdminVerified = async (userPayload) => {
        try {
            const response = await PayoutAPI.postIsUserAdminVerified(userPayload);
            if (response.Status === 200) {
                setIsUserValid(response.Data);
                setToggle(response.Data);
            }
        } catch (error) {

        }
    }
    const checkAllProcessCompleteOfPayout = (response) => {
        let i = 0;
        if (response.Data?.govtId) {
            i++;
        }
        if (response.Data?.ssn) {
            i++;
        }
        if (response.Data?.companyTaxId) {
            i++;
        }
        if (response.Data?.dob) {
            i++;
        }


        if (i == 0) {
            if (!selectedClub?.accountSetupIsCompleted && !stepperDetails?.steps[1]?.isStepCompleted) {
                updateStepper();
            }
            dispatch(stepperAction.setIsPayoutCompleted(true));
        }
    }


    const checkRequiredDocuments = async (userPayload) => {
        try {
            const response = await PayoutAPI.postAdminOnBoardingCheckDoc(userPayload);
            checkAllProcessCompleteOfPayout(response);
            setRequiredDocuments({
                dob: response.Data?.dob ? response.Data?.dob : false,
                ssn: response.Data?.ssn ? response.Data?.ssn : false,
                govtId: response.Data?.govtId ? response.Data?.govtId : false,
                companyTaxId: response.Data?.companyTaxId ? response.Data?.companyTaxId : false
            })
        } catch (error) {

        }

    }
    const getPublishKey = async () => {
        return await PaymentAPI.getPublishKey();
    }
    /// TODO /////
    const initializeStripeConnect = async (publishKey, userPayload) => {
        const stripeInstance = loadConnectAndInitialize({
            publishableKey: publishKey,
            fetchClientSecret: () => fetchClientSecret(userPayload),
            appearance: appearance
        });
        setStripeConnectInstance(stripeInstance);
        setTimeout(() => {
            setIsLoading(false);
        }, 300);
    };
    const updateIsCustomerPayFee = async () => {
        try {
            setIsLoading(true)
            await AccountSetupAPI.updateIsCustomerPayFee({
                isCustomerPayFee: !isCustomerPayFee,
                clubId: selectedClub.clubId
            });
            setIsCustomerPayFee(!isCustomerPayFee);
        } catch (error) {
            console.log(error);
        }finally{
            setIsLoading(false);
        }
    };
    const findAllPrivileges = async () => {
        try {
            setIsLoading(true);
            const reqObj = {
                userId: userInfo.userId,
                featureName: Object.keys(Privileges)[18]
            }
            const response = await PrivilegeAPI.getAllPrivilegesByFeature(reqObj);
            setPrivileges(response.Data);

        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const findIsCustomerPayProcessingFee = async () => {
        try {
            const response = await AccountSetupAPI.getAccountSetupIaCustomerPayFee(selectedClub.clubId);
            setIsCustomerPayFee(response.Data);
        } catch (error) {
            console.log(error);
        }
    };
    const fetchFeeLabels = async () => {
        try {
            const [freeFee, paidFee] = await Promise.all([
                PlaftFormAPI.getPlatformFee("FREE"),
                PlaftFormAPI.getPlatformFee("PAID"),
            ]);
            setFeeLabels({
                free: platformFeeLabel(freeFee?.Data, FEE_FALLBACK.FREE),
                paid: platformFeeLabel(paidFee?.Data, FEE_FALLBACK.PAID),
            });
        } catch (error) {
            setFeeLabels({
                free: FEE_FALLBACK.FREE,
                paid: FEE_FALLBACK.PAID,
            });
        }
    };

    useEffect(() => {
        if (userInfo.userId) {
            findAllPrivileges();
            findIsCustomerPayProcessingFee();
            fetchFeeLabels();
        }
    }, [userInfo.userid]);

    useEffect(() => {
        setIsLoading(true);


        if (userInfo.userId) {
            const userPayload = {
                userId: userInfo.userId,
                clubId: selectedClub.clubId
            }


            isUserAdminVerified(userPayload);
            checkRequiredDocuments(userPayload);
            getPublishKey().then((resp => {
                initializeStripeConnect(resp.Data, userPayload);
            }));
        }

    }, [userInfo.userId, configId]);
    return (
        <div className={typeof applySetupData === "function" ? "vendor-setup-wrap" : undefined} style={typeof applySetupData === "function" ? undefined : { minHeight: '100vh' }}>
            {isLoading && <Loader />}
            {typeof applySetupData === "function" && (
                <VendorSetupGuide
                    copy={vendorGuideCopy({
                        isFreePlan: isFreePlanSetup(stepperDetails, selectedClub),
                        freeFeeLabel: feeLabels.free,
                        paidFeeLabel: feeLabels.paid,
                    })}
                />
            )}
            {stripeConnectInstance && <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                <div className="connect-user-account-dashboard">
                    {isUserValid &&
                        <div className="payout-integration-alert">
                            {requiredDocuments.dob && <Alert severity="warning">Your Date of birth field in required please edit your dashboard and give your date of birth</Alert>}
                            {requiredDocuments.ssn && <Alert severity="warning">Please provide your full Social Security Number(SSN) please provide this and upload supporting document in dashboard account managment section</Alert>}
                            {requiredDocuments.govtId && <Alert severity="warning">Your need to provide a govt. document ID proofe for security purpose.Go to management section and click on edit buttom to upload document</Alert>}
                            {requiredDocuments.companyTaxId && <Alert severity="warning">Please provide your Employer Identification Number (EIN).If you use your Social Security number for business tax purposes, you can enter that instead.</Alert>}
                        </div>}
                    <div className="payout-integration-container">
                        <div className="payout-integration-toggle">
                            <h1>Financial Vendor</h1>
                            <CustomSwitch checked={toggle} onChange={() => setToggle(!toggle)} isDisable={isUserValid} />
                        </div>
                        {(toggle && !isUserValid) ? <div className="payout-integration-authorize">
                            <FormControl
                                fullWidth
                                size="small"
                                className="pay-intrigration-delect"
                            >
                                <Select
                                    id="demo-simple-select"
                                    value={paymentMethod}
                                    onChange={(event) => setpaymentMethod(event.target.value)}
                                >
                                    <MenuItem value={'Stripe'}>Stripe</MenuItem>
                                </Select>
                            </FormControl>
                            <p>
                                You are authorizing Stripe to handle all of your financial transactions. Please read the <Link to={'https://sportmingle.com/terms_and_conditions.html'}>Term of Services</Link>.
                            </p>
                            {(paymentMethod === 'None' || paymentMethod === '' || paymentMethod == undefined)
                                ?
                                <Link className="payout-integration-authorize-button"
                                    onClick={() => {
                                        toast.info("Plese select a payment integration")
                                    }}
                                    to="#">Authorize</Link>
                                :
                                <Link className="payout-integration-authorize-button" to="/account">Authorize</Link>}
                        </div>
                            :
                            <>
                                {isUserValid && <div className="payout-integration-info">
                                    <div className="payout-integration-method">
                                        <p>Vendor Name:</p>
                                        <span>Stripe</span>
                                    </div>
                                    <div className="payout-integration-check-authorize">
                                        <p>{selectedClub?.clubName} is authorized:</p>
                                        <span>Yes</span>
                                    </div>
                                    <div className="is-customer-pay-fee" data-testid="customer-pay-fee">
                                        <p>{CUSTOMER_PAY_FEE_COPY.label}</p>
                                        <CustomSwitch checked={isCustomerPayFee} onChange={updateIsCustomerPayFee} />
                                        <p className="is-customer-pay-fee-help">{CUSTOMER_PAY_FEE_COPY.help}</p>
                                    </div>
                                </div>}
                            </>
                        }
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {isUserValid && <PayoutDashboard privileges={privileges} />}
                    </div>
                </div>
            </ConnectComponentsProvider>}
        </div>
    );
}
export default PayoutForm;